<!--
  Client Portal v2: Team & Departments.

  Rebuilt to the v2 design (planning .../handoff_2026-06-24/
  2026-06-24-client-portal-v2/design/sections-more.jsx -- the Team function)
  and Locked Decision #6: "Team & Departments = portal users only. Client Admin
  + Store Manager are listed as team; Shoppers are consumers (seen via their
  orders), shown as a per-department attribution COUNT, not a roster."

  DEPARTMENT MANAGEMENT (Jake product-authority call 2026-06-30 -- a DELIBERATE
  departure from the v2 view-only design): Client Admins SELF-MANAGE their own
  Client's departments in-portal. The Departments panel gains ADD + per-row
  rename / archive (on the "..." menu). Distributors invoice clients for
  non-external spend, so the client is accountable for that spend and must
  self-govern department structure + budgets without escalating. Store Managers
  do NOT get these affordances (they lack portal.manage_departments AND the page
  itself 403s for them -> permission screen). Budget CAPS are managed on
  Budget.vue; allowances stay distributor-configured (out of scope).

  Layout (matches the design + screenshot):
    - Header: eyebrow "COMPANY-WIDE" + title "Team & departments" + lede + an
      Invite button (top-right, disabled stub).
    - TWO panels side by side:
        * LEFT "People with portal access": Client Admins + Store Managers.
          Each row: initials avatar, name, email, department, role tag, and a
          "..." menu affordance (people management is a later PR -- disabled).
        * RIGHT "Departments": each active department + "N shoppers". A Client
          Admin gets an "Add department" button + a per-row "..." menu with
          Rename / Archive.

  Wired to GET /api/portal/team, which returns
    { people: [{name,email,role,department,initials}],
      departments: [{id, name, shopper_count}] }
  and to the Client-Admin write endpoints:
    POST   /api/portal/departments            {name}
    PATCH  /api/portal/departments/:id        {name}
    DELETE /api/portal/departments/:id        (archive / soft-disable)
  all strictly scoped to the caller's Client (403 on a non-client caller).
-->
<template>
  <v-container fluid class="portal-team" data-testid="portal-page-team">
    <!-- Loading -->
    <template v-if="loading">
      <v-progress-linear
        indeterminate
        color="primary"
        data-testid="team-loading"
      />
    </template>

    <!-- Permission screen (Store Manager / non-admin caller -> 403) -->
    <template v-else-if="permissionDenied">
      <div class="team-header">
        <div class="team-eyebrow">Company-wide</div>
        <h1 class="text-h5 mb-1">Team &amp; departments</h1>
      </div>
      <v-card class="pa-6 text-center" data-testid="team-permission-denied">
        <v-icon size="40" class="mb-2" color="medium-emphasis">lock</v-icon>
        <div class="text-subtitle-1 mb-1">Admins only</div>
        <div class="text-body-2 text-medium-emphasis">
          Team &amp; departments is managed by your Client Admins. Ask an admin
          on your account if you need a teammate added or a department changed.
        </div>
      </v-card>
    </template>

    <!-- Error -->
    <template v-else-if="errorMsg">
      <h1 class="text-h5 mb-4">Team &amp; departments</h1>
      <v-alert type="error" variant="tonal" data-testid="team-error">
        {{ errorMsg }}
        <div class="mt-2">
          <PortalButton
            variant="ghost"
            size="sm"
            data-testid="team-retry"
            @click="fetchTeam"
          >
            Try again
          </PortalButton>
        </div>
      </v-alert>
    </template>

    <!-- ===================== MAIN VIEW ===================== -->
    <template v-else>
      <div class="team-header">
        <div class="team-header__top">
          <div>
            <div class="team-eyebrow">Company-wide</div>
            <h1 class="text-h5 mb-1">Team &amp; departments</h1>
            <p class="text-body-2 text-medium-emphasis mb-0 team-lede">
              Who can sign in to manage your stores, and the departments your
              spend rolls up to. Department structure drives your budget
              tracking.
            </p>
          </div>
          <!-- Invite button -- visible to Client Admins (portal.manage_own_team). -->
          <PortalButton
            v-if="canManageTeam"
            variant="primary"
            data-testid="team-invite-button"
            @click="openInviteDialog"
          >
            <template #icon>
              <v-icon size="18">person_add</v-icon>
            </template>
            Invite
          </PortalButton>
        </div>
      </div>

      <!-- Inline action feedback (create / rename / archive / invite). -->
      <v-alert
        v-if="actionMsg"
        :type="actionType"
        variant="tonal"
        class="mb-3"
        density="compact"
        data-testid="team-action-msg"
      >
        {{ actionMsg }}
      </v-alert>

      <div class="team-grid">
        <!-- LEFT: People with portal access -->
        <v-card class="team-panel" data-testid="team-people-panel">
          <div class="team-panel__head">
            <div class="team-panel__title">People with portal access</div>
            <div class="team-panel__sub">
              Client Admins and Store Managers -- the people who sign in.
            </div>
          </div>

          <div class="team-panel__body">
            <div
              v-if="!people.length"
              class="team-empty"
              data-testid="team-people-empty"
            >
              It&apos;s just you so far.
            </div>
            <div
              v-for="(person, idx) in people"
              :key="idx"
              class="team-person"
              :data-testid="`team-person-${idx}`"
            >
              <span
                class="team-avatar"
                :class="{
                  'team-avatar--manager': person.role === 'Store Manager',
                }"
                aria-hidden="true"
                >{{ person.initials }}</span
              >
              <div class="team-person__id">
                <div class="team-person__name">{{ person.name }}</div>
                <div class="team-person__email">{{ person.email }}</div>
              </div>
              <span class="team-person__dept">
                {{ person.department || "--" }}
              </span>
              <span
                class="team-role-tag"
                :class="{
                  'team-role-tag--manager': person.role === 'Store Manager',
                }"
                :data-testid="`team-role-${idx}`"
              >
                {{ person.role }}
              </span>
              <!-- "..." menu affordance: non-functional (people mgmt later). -->
              <button
                type="button"
                class="team-person__menu"
                aria-label="Manage (coming soon)"
                disabled
                :data-testid="`team-menu-${idx}`"
              >
                <v-icon size="20" color="medium-emphasis">more_horiz</v-icon>
              </button>
            </div>
          </div>

          <div class="team-panel__footer" data-testid="team-people-footer">
            Teammates sign in with their work email and password. Managers see
            all orders for their store(s); shoppers buy from your stores but
            don&apos;t access this portal.
          </div>
        </v-card>

        <!-- RIGHT: Departments.
             Feature gate (SA-global ``enable_departments``): the whole card
             is a Departments surface, so OFF removes it entirely rather
             than leaving an empty shell. Read through the ONE shared
             composable -- ``canManageDepartments`` below is a CAPABILITY
             and answers a different question (may THIS user manage them),
             which is why the capability alone was not enough. -->
        <v-card
          v-if="enableDepartments"
          class="team-panel"
          data-testid="team-departments-panel"
        >
          <div class="team-panel__head team-panel__head--row">
            <div>
              <div class="team-panel__title">Departments</div>
              <div class="team-panel__sub">
                Org structure that feeds Budget. Counts are shoppers attributed
                for spend tracking.
              </div>
            </div>
            <!-- Client-Admin ONLY: add a department. Hidden entirely for a
                 caller without the manage cap. -->
            <PortalButton
              v-if="canManageDepartments"
              variant="secondary"
              size="sm"
              data-testid="dept-add-button"
              @click="openCreate"
            >
              <template #icon><v-icon size="16">add</v-icon></template>
              Add department
            </PortalButton>
          </div>

          <div class="team-panel__body">
            <div
              v-if="!departments.length"
              class="team-empty"
              data-testid="team-departments-empty"
            >
              No departments yet.
            </div>
            <div
              v-for="(dept, idx) in departments"
              :key="dept.id ?? idx"
              class="team-dept"
              :data-testid="`team-dept-${idx}`"
            >
              <span class="team-dept__name">{{ dept.name }}</span>
              <span
                class="team-dept__count"
                :data-testid="`team-dept-count-${idx}`"
              >
                {{ dept.shopper_count }}
                {{ dept.shopper_count === 1 ? "shopper" : "shoppers" }}
              </span>
              <!-- Client-Admin ONLY: per-row "..." menu -> rename / archive. -->
              <button
                v-if="canManageDepartments"
                type="button"
                class="team-dept__menu"
                aria-label="Manage department"
                :data-testid="`dept-menu-${idx}`"
                @click="toggleMenu(dept.id)"
              >
                <v-icon size="20" color="medium-emphasis">more_horiz</v-icon>
              </button>

              <!-- Inline action row (toggled by the "..." menu). Inline (not a
                   v-menu) so it is fully mount-testable + keyboard-simple. -->
              <div
                v-if="canManageDepartments && openMenuId === dept.id"
                class="team-dept__actions"
                :data-testid="`dept-actions-${idx}`"
              >
                <PortalButton
                  variant="ghost"
                  size="sm"
                  :data-testid="`dept-rename-${idx}`"
                  @click="openRename(dept)"
                >
                  Rename
                </PortalButton>
                <PortalButton
                  variant="ghost"
                  size="sm"
                  :data-testid="`dept-archive-${idx}`"
                  :loading="archivingId === dept.id"
                  @click="archiveDept(dept)"
                >
                  Archive
                </PortalButton>
              </div>
            </div>
          </div>
        </v-card>
      </div>

      <!-- ===== Create / Rename dialog (shared form) ===== -->
      <v-dialog
        v-model="dialogOpen"
        max-width="440"
        data-testid="dept-dialog"
      >
        <v-card class="pa-5">
          <div class="text-subtitle-1 mb-1">
            {{ dialogMode === "rename" ? "Rename department" : "Add department" }}
          </div>
          <div class="text-body-2 text-medium-emphasis mb-3">
            {{
              dialogMode === "rename"
                ? "Update this department's name. Spend history keeps its original name."
                : "Departments group your spend for budget tracking."
            }}
          </div>
          <v-text-field
            v-model="dialogName"
            label="Department name"
            variant="outlined"
            density="compact"
            data-testid="dept-name-input"
            @keyup.enter="submitDialog"
          />
          <div
            v-if="dialogError"
            class="text-body-2 dept-dialog__error mb-2"
            data-testid="dept-dialog-error"
          >
            {{ dialogError }}
          </div>
          <div class="dept-dialog__actions">
            <PortalButton
              variant="ghost"
              size="sm"
              data-testid="dept-dialog-cancel"
              @click="closeDialog"
            >
              Cancel
            </PortalButton>
            <PortalButton
              variant="primary"
              size="sm"
              :loading="dialogSaving"
              data-testid="dept-dialog-save"
              @click="submitDialog"
            >
              {{ dialogMode === "rename" ? "Save" : "Create" }}
            </PortalButton>
          </div>
        </v-card>
      </v-dialog>

      <!-- Invite teammate dialog -->
      <v-dialog
        v-model="inviteDialogOpen"
        max-width="440"
        data-testid="team-invite-dialog"
      >
        <v-card class="pa-4">
          <div class="text-subtitle-1 font-weight-medium mb-2">
            Invite a teammate
          </div>
          <div
            class="text-body-2 text-medium-emphasis mb-4"
            data-testid="invite-role-copy"
          >
            They will join as a <strong>Client Admin</strong> with full
            portal access, including team and budget management. They will
            receive an email with a link to set up their account.
            Invitations expire after 14 days.
          </div>
          <v-text-field
            v-model="inviteEmail"
            label="Email address"
            variant="outlined"
            density="compact"
            type="email"
            autocomplete="off"
            class="mb-2"
            data-testid="invite-email-input"
            @keyup.enter="submitInvite"
          />
          <v-text-field
            v-model="inviteFirstName"
            label="First name (optional)"
            variant="outlined"
            density="compact"
            autocomplete="off"
            class="mb-2"
            data-testid="invite-firstname-input"
            @keyup.enter="submitInvite"
          />
          <div
            v-if="inviteError"
            class="text-body-2 mb-2"
            style="color: rgb(var(--v-theme-error))"
            data-testid="invite-error"
          >
            {{ inviteError }}
          </div>
          <div class="dept-dialog__actions">
            <PortalButton
              variant="ghost"
              size="sm"
              data-testid="invite-cancel"
              @click="closeInviteDialog"
            >
              Cancel
            </PortalButton>
            <PortalButton
              variant="primary"
              size="sm"
              :loading="inviteSaving"
              data-testid="invite-send"
              @click="submitInvite"
            >
              Send invite
            </PortalButton>
          </div>
        </v-card>
      </v-dialog>
    </template>
  </v-container>
</template>

<script>
import axios from "@/axios";
import PortalButton from "@/components/portal/PortalButton.vue";
import { useAccountStore } from "@/stores/account";
import { useEnableDepartments } from "@/composables/useEnableDepartments";
import { emitSidebarRefresh } from "@/stores/sidebarRefresh";

export default {
  name: "PortalTeam",
  components: { PortalButton },
  setup() {
    // Departments feature gate (SA-global). Defaults TRUE so the card never
    // flickers out while the GET is in flight, and stays visible if the read
    // fails -- hiding a configured client's departments on a transient error
    // is the worse failure.
    const { enableDepartments } = useEnableDepartments();
    return { enableDepartments };
  },
  data() {
    return {
      people: [],
      departments: [],
      loading: true,
      errorMsg: "",
      permissionDenied: false,
      // Which department's "..." action row is open (id), or null.
      openMenuId: null,
      // Dialog state (shared by create + rename).
      dialogOpen: false,
      dialogMode: "create", // "create" | "rename"
      dialogName: "",
      dialogError: "",
      dialogSaving: false,
      dialogDeptId: null, // rename target
      // Archive-in-flight guard (per department).
      archivingId: null,
      // Post-action banner. actionType is a Vuetify alert type:
      // 'success' | 'warning' | 'error'.
      actionMsg: "",
      actionType: "success",
      // Invite dialog state.
      inviteDialogOpen: false,
      inviteEmail: "",
      inviteFirstName: "",
      inviteError: "",
      inviteSaving: false,
    };
  },
  computed: {
    // Client-Admin-only affordances gate on the NEW self-service capability
    // (Jake 2026-06-30). A Store Manager lacks it (and the page 403s for them
    // anyway); an Owner-tier viewer would also lack it here because it holds no
    // client scope. Reactive: re-renders when the account store hydrates.
    canManageDepartments() {
      const account = useAccountStore();
      return account.capabilities.includes("portal.manage_departments");
    },
    // Invite button: gated on portal.manage_own_team (Client Admin only).
    canManageTeam() {
      const account = useAccountStore();
      return account.capabilities.includes("portal.manage_own_team");
    },
  },
  created() {
    this.fetchTeam();
  },
  methods: {
    async fetchTeam() {
      this.loading = true;
      this.errorMsg = "";
      this.permissionDenied = false;
      try {
        const resp = await axios.get("/api/portal/team");
        this.people = resp?.data?.people ?? [];
        this.departments = resp?.data?.departments ?? [];
      } catch (e) {
        if (e?.response?.status === 403) {
          this.permissionDenied = true;
        } else {
          this.errorMsg = "Could not load your team.";
        }
      } finally {
        this.loading = false;
      }
    },
    toggleMenu(deptId) {
      this.openMenuId = this.openMenuId === deptId ? null : deptId;
    },
    openCreate() {
      this.dialogMode = "create";
      this.dialogName = "";
      this.dialogDeptId = null;
      this.dialogError = "";
      this.dialogOpen = true;
    },
    openRename(dept) {
      this.dialogMode = "rename";
      this.dialogName = dept.name;
      this.dialogDeptId = dept.id;
      this.dialogError = "";
      this.dialogOpen = true;
      this.openMenuId = null;
    },
    closeDialog() {
      this.dialogOpen = false;
      this.dialogSaving = false;
    },
    async submitDialog() {
      const name = (this.dialogName || "").trim();
      if (!name) {
        this.dialogError = "Enter a department name.";
        return;
      }
      if (this.dialogSaving) return;
      this.dialogSaving = true;
      this.dialogError = "";
      try {
        if (this.dialogMode === "rename") {
          await axios.patch(`/api/portal/departments/${this.dialogDeptId}`, {
            name,
          });
          this.flash("Department renamed.", true);
        } else {
          await axios.post("/api/portal/departments", { name });
          this.flash("Department added.", true);
        }
        this.dialogOpen = false;
        await this.fetchTeam();
        // Refresh the sidebar sub-text (team_access_count) live (#1830).
        // Generic delegated mechanism (PR-E): the portalNavSummary store
        // subscribes and refetches.
        emitSidebarRefresh("team");
      } catch (e) {
        const status = e?.response?.status;
        if (status === 409) {
          this.dialogError = "A department with that name already exists.";
        } else if (status === 400) {
          this.dialogError = "Enter a valid department name.";
        } else {
          this.dialogError = "Could not save. Please try again.";
        }
      } finally {
        this.dialogSaving = false;
      }
    },
    async archiveDept(dept) {
      if (this.archivingId) return;
      this.archivingId = dept.id;
      try {
        await axios.delete(`/api/portal/departments/${dept.id}`);
        this.openMenuId = null;
        this.flash(`Archived ${dept.name}.`, true);
        await this.fetchTeam();
        // Refresh the sidebar sub-text (team_access_count) live (#1830).
        // Generic delegated mechanism (PR-E): the portalNavSummary store
        // subscribes and refetches.
        emitSidebarRefresh("team");
      } catch (e) {
        this.flash("Could not archive that department.", false);
      } finally {
        this.archivingId = null;
      }
    },
    // flash(msg, type) where type is 'success' | 'warning' | 'error'.
    // Accepts a legacy boolean for backward compat with existing callers
    // (true -> 'success', false -> 'error').
    flash(msg, type) {
      this.actionMsg = msg;
      if (type === true || type === undefined) {
        this.actionType = "success";
      } else if (type === false) {
        this.actionType = "error";
      } else {
        this.actionType = type;
      }
    },
    // ----- Invite dialog -----
    openInviteDialog() {
      this.inviteEmail = "";
      this.inviteFirstName = "";
      this.inviteError = "";
      this.inviteSaving = false;
      this.inviteDialogOpen = true;
    },
    closeInviteDialog() {
      this.inviteDialogOpen = false;
      this.inviteSaving = false;
    },
    async submitInvite() {
      const email = (this.inviteEmail || "").trim();
      if (!email || !email.includes("@")) {
        this.inviteError = "Enter a valid email address.";
        return;
      }
      if (this.inviteSaving) return;
      this.inviteSaving = true;
      this.inviteError = "";
      try {
        const resp = await axios.post("/api/portal/team/invite", {
          email,
          first_name: (this.inviteFirstName || "").trim() || undefined,
        });
        this.inviteDialogOpen = false;
        if (resp.data && resp.data.warning) {
          // The invitation row was persisted but the email send failed.
          // Surface the warning so the user is not left assuming the
          // email was delivered when it was not.
          this.flash(
            "Invitation saved, but the email could not be sent. " +
              "Please try again or contact support.",
            "warning",
          );
        } else {
          this.flash(`Invite sent to ${email}.`, "success");
        }
        await this.fetchTeam();
        // Refresh the sidebar sub-text (team_access_count) live. Generic
        // delegated mechanism (PR-E): the portalNavSummary store subscribes
        // and refetches.
        emitSidebarRefresh("team");
      } catch (e) {
        const status = e?.response?.status;
        if (status === 429) {
          // 60-second cooldown: a duplicate invite was just sent.
          this.inviteError =
            "An invitation was just sent to that address. Please wait a moment before resending.";
        } else if (status === 400) {
          const msg = e?.response?.data?.error || "Invalid email address.";
          this.inviteError = msg;
        } else {
          this.inviteError = "Could not send invitation. Please try again.";
        }
      } finally {
        this.inviteSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.team-header {
  margin-bottom: 22px;
}
.team-header__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.team-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: var(--tf-brand-ink, #1565c0);
  margin-bottom: 6px;
}
.team-lede {
  max-width: 560px;
}
.team-invite-wrap {
  display: inline-flex;
  flex-shrink: 0;
}

.team-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
}
@media (max-width: 900px) {
  .team-grid {
    grid-template-columns: 1fr;
  }
}

.team-panel {
  padding: 18px;
}
.team-panel__head {
  margin-bottom: 14px;
}
.team-panel__head--row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.team-panel__title {
  font-size: 15px;
  font-weight: 600;
  color: var(--tf-fg-1, #212121);
}
.team-panel__sub {
  font-size: 12.5px;
  color: var(--tf-fg-4, #757575);
  margin-top: 3px;
  line-height: 1.45;
}
.team-panel__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.team-panel__footer {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--tf-border, #e0e0e0);
  font-size: 12px;
  color: var(--tf-fg-4, #757575);
  line-height: 1.5;
}

/* People rows */
.team-person {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--tf-surface-alt, #fafbfc);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 10px;
}
.team-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--tf-on-brand, #ffffff);
  background: var(--portal-brand-color, var(--tf-brand, #1565c0));
}
.team-avatar--manager {
  background: var(--tf-fg-3, #616161);
}
.team-person__id {
  flex: 1;
  min-width: 0;
}
.team-person__name {
  font-size: 14px;
  font-weight: 500;
  color: var(--tf-fg-1, #212121);
}
.team-person__email {
  font-size: 12px;
  color: var(--tf-fg-4, #757575);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.team-person__dept {
  font-size: 12px;
  color: var(--tf-fg-3, #616161);
  min-width: 78px;
}
.team-role-tag {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 9999px;
  white-space: nowrap;
  color: var(--tf-brand-ink, #1565c0);
  background: var(--tf-brand-soft, #e8f0fb);
}
.team-role-tag--manager {
  color: var(--tf-fg-2, #424242);
  background: var(--tf-surface-alt, #eceff1);
}
.team-person__menu {
  border: none;
  background: transparent;
  cursor: not-allowed;
  padding: 4px;
  display: inline-flex;
  align-items: center;
}

/* Department rows */
.team-dept {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding: 9px 0;
  border-top: 1px solid var(--tf-border, #e0e0e0);
}
.team-dept:first-child {
  border-top: none;
}
.team-dept__name {
  font-size: 13.5px;
  color: var(--tf-fg-1, #212121);
}
.team-dept__count {
  font-size: 12px;
  color: var(--tf-fg-4, #757575);
  margin-left: auto;
}
.team-dept__menu {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
}
.team-dept__actions {
  display: flex;
  gap: 8px;
  width: 100%;
  margin-top: 6px;
  justify-content: flex-end;
}

.team-empty {
  font-size: 13px;
  color: var(--tf-fg-4, #757575);
  padding: 8px 0;
}

.dept-dialog__error {
  color: var(--tf-error, #ef0405);
}
.dept-dialog__actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}
</style>
